import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Eye,
  FileText,
  DollarSign,
  Percent,
  Mail,
  MessageSquare,
  Copy,
  Edit,
  Trash2,
  ShoppingCart
} from 'lucide-react';
import {
  DropdownContainer,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  DropdownIcon
} from './style';
import threeDotsIcon from '../../../../../assets/three-dots-icon.svg';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import CloseSaleFashionModal from '../CloseSaleFashionModal';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import ModalContext from '../../../../../contexts/ModalContext';
import axios from '../../../../../config/axios';
import FashionApiService from '../../../../../services/FashionApiService';

const ORDERS_URL = `${process.env.REACT_APP_GATEWAY_BASE_URL}/steel-orders`

interface FashionOrder {
  id: number;
  saleStatus: string;
  status: boolean;
}

interface ActionsDropdownProps {
  order: FashionOrder;
  onDelete: () => void;
  onUpdate: () => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ order, onDelete, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number; direction: 'up' | 'down' }>({ top: 0, left: 0, width: 0, direction: 'down' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseSaleModal, setShowCloseSaleModal] = useState(false);
  const [showRevertSaleModal, setShowRevertSaleModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { setFeedback } = useContext(FeedbackContext);
  const { setModalActive, setModalSection, setSelectedFashionOrderId } = useContext(ModalContext);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (buttonRef.current && buttonRef.current.contains(target)) ||
        (dropdownRef.current && dropdownRef.current.contains(target))
      ) {
        return;
      }
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleScrollOrResize = () => {
      setIsOpen(false);
    };
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!isOpen && buttonRef.current) {
      const buttonElement = buttonRef.current;
      const rowElement = buttonElement.closest('tr') as HTMLElement | null;

      const buttonRect = buttonElement.getBoundingClientRect();
      const rowRect = rowElement?.getBoundingClientRect();

      const menuHeight = 400;
      const menuWidth = 200;

      const referenceTop = rowRect ? rowRect.top : buttonRect.top;
      const referenceBottom = rowRect ? rowRect.bottom : buttonRect.bottom;

      const spaceBelow = window.innerHeight - referenceBottom;
      const spaceAbove = referenceTop;

      let direction: 'up' | 'down' = 'down';
      let top = referenceBottom;

      if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
        direction = 'up';
        top = referenceTop - menuHeight;
      } else {
        direction = 'down';
        top = referenceBottom;
      }

      const left =
        buttonRect.left + buttonRect.width / 2 - menuWidth / 2;

      setDropdownPosition({
        top: Math.round(top),
        left: Math.round(left),
        width: menuWidth,
        direction
      });
    }

    setIsOpen(!isOpen);
  };

  const handleAction = (action: string) => {
    setIsOpen(false);

    switch (action) {
      case 'view':
        setSelectedFashionOrderId(order.id);
        setModalSection('viewFashionOrder');
        setModalActive(true);
        break;
      case 'edit':
        navigate(`/panel/fashion/edit/${order.id}`);
        break;
      case 'close-sale':
        setShowCloseSaleModal(true);
        break;
      case 'revert-sale':
        setShowRevertSaleModal(true);
        break;
      case 'generate-pdf':
        setSelectedFashionOrderId(order.id);
        setModalSection('generateFashionOrderPdf');
        setModalActive(true);
        break;
      case 'invoice':

        break;
      case 'commission':

        break;
      case 'send-email':

        break;
      case 'send-whatsapp':

        break;
      case 'duplicate':

        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      await axios.delete(
        `${ORDERS_URL}/order/${order.id}`
      );
      setFeedback({ message: `Pedido ${order.id} excluído com sucesso!`, isOpen: true, statusOk: true });
      setShowDeleteModal(false);
      onDelete();
    } catch (error: any) {
      console.error('Erro ao excluir pedido:', error);
      setFeedback({ message: 'Erro ao excluir pedido. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleConfirmCloseSale = async () => {
    try {
      setIsLoading(true);
      await axios.patch(
        `${ORDERS_URL}/order/${order.id}`,
        { saleStatus: 'Fechado' }
      );

      try {
        const response = await FashionApiService.generateProductionOrdersFromOrder(order.id);
        const createdOps = response.data || [];
        for (const op of createdOps) {
          if (op.id) {
            await axios.post(
              `${process.env.REACT_APP_GATEWAY_BASE_URL}/backend-fashion-orders/fashion-production-internal/create-sales-stock-items`,
              { serviceId: op.id, tenantId: op.tenantId }
            );
          }
        }
      } catch (opError: any) {
        console.error('Erro ao gerar ordens de produção:', opError);
      }

      setFeedback({ message: `Venda do pedido ${order.id} fechada com sucesso!`, isOpen: true, statusOk: true });
      setShowCloseSaleModal(false);
      onUpdate();
    } catch (error: any) {
      console.error('Erro ao fechar venda:', error);
      setFeedback({ message: 'Erro ao fechar venda. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSale = async () => {
    try {
      setIsLoading(true);
      await axios.patch(
        `${ORDERS_URL}/order/${order.id}`,
        { saleStatus: 'Cancelado' }
      );
      setFeedback({ message: `Venda do pedido ${order.id} cancelada!`, isOpen: true, statusOk: true });
      setShowCloseSaleModal(false);
      onUpdate();
    } catch (error: any) {
      console.error('Erro ao cancelar venda:', error);
      setFeedback({ message: 'Erro ao cancelar venda. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRevertSale = async () => {
    try {
      setIsLoading(true);
      if (order.saleStatus === 'Cancelado') {
        await axios.patch(
          `${ORDERS_URL}/order/${order.id}`,
          { saleStatus: 'Em Orçamento' }
        );
      } else {
        await FashionApiService.revertFashionSale(order.id);
      }
      setFeedback({ message: `Venda do pedido ${order.id} revertida com sucesso!`, isOpen: true, statusOk: true });
      setShowRevertSaleModal(false);
      onUpdate();
    } catch (error: any) {
      console.error('Erro ao reverter venda:', error);
      setFeedback({ message: error?.response?.data?.message || 'Erro ao reverter venda. Tente novamente.', isOpen: true, statusOk: false });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelCloseSale = () => {
    setShowCloseSaleModal(false);
  };

  const canCloseSale = order.saleStatus === 'Em Orçamento' || order.saleStatus === 'Em orçamento' || order.saleStatus === 'Em andamento';
  const canRevertSale = order.saleStatus === 'Fechado' || order.saleStatus === 'Cancelado';

  return (
    <>
      <DropdownContainer>
        <DropdownButton ref={buttonRef} onClick={handleToggleDropdown}>
          <DropdownIcon src={threeDotsIcon} alt="Opções" />
        </DropdownButton>
      </DropdownContainer>
      {isOpen && ReactDOM.createPortal(
        <DropdownMenu
          ref={dropdownRef}
          top={dropdownPosition.top}
          left={dropdownPosition.left}
          width={dropdownPosition.width}
          direction={dropdownPosition.direction}
        >
          {canCloseSale && (
            <DropdownItem onClick={() => handleAction('close-sale')}>
              <ShoppingCart size={16} />
              Fechar venda
            </DropdownItem>
          )}
          {canRevertSale && (
            <DropdownItem onClick={() => handleAction('revert-sale')}>
              <ShoppingCart size={16} />
              Reverter venda
            </DropdownItem>
          )}
          <DropdownItem onClick={() => handleAction('generate-pdf')}>
            <FileText size={16} />
            Gerar PDF
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('invoice')}>
            <DollarSign size={16} />
            Faturar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('commission')}>
            <Percent size={16} />
            Comissão
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('send-email')}>
            <Mail size={16} />
            Enviar por e-mail
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('send-whatsapp')}>
            <MessageSquare size={16} />
            Enviar por Whatsapp
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('view')}>
            <Eye size={16} />
            Visualizar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('duplicate')}>
            <Copy size={16} />
            Duplicar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('edit')}>
            <Edit size={16} />
            Editar
          </DropdownItem>
          <DropdownItem onClick={() => handleAction('delete')}>
            <Trash2 size={16} />
            Excluir
          </DropdownItem>
        </DropdownMenu>,
        document.body
      )}
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Pedido"
        message={`Tem certeza que deseja excluir o pedido ${order.id}? Esta ação não pode ser desfeita.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: handleCancelDelete,
            variant: 'outline'
          },
          {
            label: isLoading ? 'Excluindo...' : 'Excluir',
            onClick: handleConfirmDelete,
            variant: 'danger'
          }
        ]}
      />
      <ConfirmationModal
        isOpen={showRevertSaleModal}
        title="Reverter Venda"
        message={`Tem certeza que deseja reverter a venda do pedido ${order.id}? Todas as ordens de produção e itens de estoque de vendas correspondentes serão excluídos permanentemente.`}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowRevertSaleModal(false),
            variant: 'outline'
          },
          {
            label: isLoading ? 'Revertendo...' : 'Reverter',
            onClick: handleConfirmRevertSale,
            variant: 'danger'
          }
        ]}
      />
      <CloseSaleFashionModal
        isOpen={showCloseSaleModal}
        onClose={handleCancelCloseSale}
        onConfirm={handleConfirmCloseSale}
        onCancelSale={handleCancelSale}
        orderId={order.id}
        isLoading={isLoading}
      />
    </>
  );
};

export default ActionsDropdown;
